import { BrandConfig } from './types';
import expRetry from '@ringcentral/rwc-utils/src/exponential-retry';
import { DEFAULT_PANEL_COLOR } from './constant';
import { getLocale } from '@rwc/utils/locale';
import {
    DynamicBrandingErrorReport,
    handleErrorReport,
} from '@ringcentral/rwc-utils/src/error-report';
import { getPrefixedLogger, prefixes } from '@ringcentral/rwc-utils/src/rwc-logger';

const FETCH_BSS_ERROR = {
    BSS_BASE_ERROR: 'fetch bss base config failed',
    BSS_LANG_ERROR: 'fetch bss lang config failed',
};

const responseIsError = response => response instanceof Error;

const logger = getPrefixedLogger(prefixes.VC_LOW, 'bss-manager');

export type Options = {
    brandId: string;
    lang: string;
};
type RawBssConfig = {
    themes: Array<Record<string, string>>;
} & Record<string, string>;
export class BssManager {
    public config: Partial<BrandConfig> = {};

    private brandId = '';

    private lang = 'en-US';

    private bssServerUrl = '';

    private static FALLBACK_LOCALE = 'en-US';

    constructor(options: Options) {
        this.brandId = options.brandId;
        this.lang = getLocale(options.lang);
    }

    private addBrandBaseUrl(url: string) {
        return url ? `${this.bssServerUrl}${url}` : '';
    }

    /**
     * fetch bss base config from bss
     */
    async fetchBssBaseConfig(): Promise<RawBssConfig> {
        const response = await fetch(`${this.bssServerUrl}/base.json`);
        if (!response.ok) {
            const error = new Error(FETCH_BSS_ERROR.BSS_BASE_ERROR);

            const report = new DynamicBrandingErrorReport(error, {
                brandId: this.brandId,
                extra: {
                    endpoint: '/base.json',
                    rawEndpoint: response?.url,
                    status: response?.status,
                    response: response,
                },
                type: 'GetBSSFailed',
            });

            handleErrorReport(report);
            throw error;
        }

        const data = await response.json();
        return data;
    }

    /**
     * fetch bss locale config
     */
    async fetchBssLangConfig(): Promise<RawBssConfig> {
        const response = await fetch(`${this.bssServerUrl}/${this.lang}.json`);
        if (!response.ok) {
            const error = new Error(FETCH_BSS_ERROR.BSS_LANG_ERROR);

            const report = new DynamicBrandingErrorReport(error, {
                brandId: this.brandId,
                extra: {
                    endpoint: `/${this.lang}.json`,
                    rawEndpoint: response?.url,
                    status: response?.status,
                    response: response,
                },
                type: 'GetBSSFailed',
            });

            handleErrorReport(report);
            throw error;
        }

        const data = await response.json();
        return data;
    }

    /**
     * fetch bss en-US locale config in case of the current locale config is not found
     */
    async fetchFallbackBssLangConfig(): Promise<RawBssConfig> {
        const response = await fetch(`${this.bssServerUrl}/${BssManager.FALLBACK_LOCALE}.json`);
        if (!response.ok) {
            const error = new Error(FETCH_BSS_ERROR.BSS_LANG_ERROR);

            const report = new DynamicBrandingErrorReport(error, {
                brandId: this.brandId,
                extra: {
                    endpoint: `/${BssManager.FALLBACK_LOCALE}.json`,
                    rawEndpoint: response?.url,
                    status: response?.status,
                    response: response,
                },
                type: 'GetBSSFailed',
            });

            handleErrorReport(report);
            throw error;
        }

        const data = await response.json();
        return data;
    }

    formatBssConfig(
        rawBaseConfig: RawBssConfig,
        rawLangConfig: RawBssConfig
    ): Partial<BrandConfig> {
        const baseDefaultTheme = rawBaseConfig.themes.find(theme =>
            ['true', true].includes(theme.isDefault)
        );
        const langDefaultTheme = baseDefaultTheme
            ? rawLangConfig.themes.find(theme => theme.themeId === baseDefaultTheme.themeId)
            : {};
        const finalDefaultTheme = { ...baseDefaultTheme, ...langDefaultTheme };
        const finalBssConfig = {
            ...rawBaseConfig,
            ...rawLangConfig,
        };
        finalBssConfig.themes = [];
        const config = {
            brandId: this.brandId as string,
            displayName: finalBssConfig.displayName ?? '',
            productName: finalBssConfig.videoProductName ?? '',
            shortDisplayName: finalBssConfig.shortDisplayName ?? '',
            notificationAppPrefixName: finalBssConfig.notificationAppPrefixName ?? '',
            copyright: finalBssConfig.copyright ?? '',
            notificationTradeMark: finalBssConfig.notificationTradeMark ?? '',
            legalInfoPage: finalBssConfig.legalInfoPage ?? '',
            termsOfServicePage: finalBssConfig.termsOfServicePage ?? '',
            privacyPolicyPage: finalBssConfig.privacyPolicyPage ?? '',
            trustCenterPage: finalBssConfig.trustCenterPage ?? '',
            supportRootPage: finalBssConfig.videoSupportRootPage ?? '',
            privacyControlsHelpPage: finalBssConfig.privacyControlsHelpPage ?? '',
            launchYear: finalBssConfig.launchYear,
            knowledgeBase: {
                systemRequirementsLink:
                    finalBssConfig.videoFeaturesSystemRequirementsHelpPage ?? '',
                chromaKeyLink: finalBssConfig.videoFeaturesVirtualBackgroundHelpPage ?? '',
                hardwareAccelerationLink: finalBssConfig.videoFeaturesHwAccelerationHelpPage ?? '',
                thirdPartyCameraLink: finalBssConfig.videoFeaturesThirdPartyCameraLink ?? '',
            },
            assets: {
                mainLogoUrl: this.addBrandBaseUrl(finalDefaultTheme?.serviceSiteMainLogo),
                faviconUrl: this.addBrandBaseUrl(finalDefaultTheme?.faviconVideo),
                blackLogoUrl: this.addBrandBaseUrl(finalDefaultTheme?.videoAboutLogo),
                topPanelLogoUrl: this.addBrandBaseUrl(finalDefaultTheme?.videoSiteMainLogo),
                watermarkUrl: this.addBrandBaseUrl(finalDefaultTheme?.['appWatermarkIOS@2X']),
                poweredByLogoUrl: this.addBrandBaseUrl(finalDefaultTheme?.partnershipLabelLogo),
                videoAboutIcon: this.addBrandBaseUrl(finalDefaultTheme?.videoAboutIcon),
                topPanelColor:
                    finalDefaultTheme?.additionalBrandColor120 ||
                    DEFAULT_PANEL_COLOR.TOP_PANEL_COLOR,
                settingColor:
                    finalDefaultTheme?.additionalBrandColor121 ||
                    DEFAULT_PANEL_COLOR.SETTING_ACTIVE_COLOR,
                settingActiveColor:
                    finalDefaultTheme?.additionalBrandColor122 ||
                    DEFAULT_PANEL_COLOR.SETTING_ACTIVE_COLOR,
                settingActiveBgColor:
                    finalDefaultTheme?.additionalBrandColor123 ||
                    DEFAULT_PANEL_COLOR.SETTING_ACTIVE_BG_COLOR,
            },
        };
        return config;
    }

    async fetchBssConfig(bssServerUrl: string) {
        return new Promise<void>((resolve, reject) => {
            this.bssServerUrl = bssServerUrl;
            try {
                Promise.all([
                    expRetry(() => this.fetchBssBaseConfig(), {
                        retryRule: responseIsError,
                        retryMaxCallsCount: 3,
                        onMaxCallsRunOut: () => {
                            reject(new Error(FETCH_BSS_ERROR.BSS_BASE_ERROR));
                        },
                    }),
                    expRetry(() => this.fetchBssLangConfig(), {
                        retryRule: responseIsError,
                        retryMaxCallsCount: 3,
                        onMaxCallsRunOut: () => {
                            logger.info(
                                `[BSS] fetching BSS locale config failed, fallback to ${BssManager.FALLBACK_LOCALE}`
                            );
                        },
                    }).catch(() =>
                        // if lang config is not found, we will fallback to en-US config
                        expRetry(() => this.fetchFallbackBssLangConfig(), {
                            retryRule: responseIsError,
                            retryMaxCallsCount: 3,
                            onMaxCallsRunOut: () => {
                                reject(new Error(FETCH_BSS_ERROR.BSS_LANG_ERROR));
                            },
                        })
                    ),
                ])
                    .then(([bssConfig, bssLocaleConfig]) => {
                        this.config = this.formatBssConfig(
                            bssConfig as RawBssConfig,
                            bssLocaleConfig as RawBssConfig
                        );
                        resolve();
                    })
                    .catch(e => reject(e));
            } catch (error) {
                const e = error as any; // TODO: RCV-99509 fix ts after update ts
                reject(e);
            }
        });
    }

    getConfig() {
        return this.config;
    }
}
